import { useUserEventLogger } from '@repo/tracking-service';
import cn from 'clsx';
import { useAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';

import { employmentSpecialRelationshipsCurrentQuery } from '@/__generated__/employmentSpecialRelationshipsCurrentQuery.graphql';
import { SurveyTopNavigation } from '@/components/survey/common/TopNavigation';
import { useSurveyUserEventAttributes } from '@/components/survey/common/hooks/use-survey-user-event-attributes';
import { CTA, WithButtonList } from '@/components/survey/common/ui';
import listStyle from '@/components/survey/common/ui/styles/WithButtonList.module.scss';
import common from '@/components/survey/common/ui/styles/common.module.scss';
import { birthToYYMD } from '@/components/survey/common/utils';
import { SpecialRelationshipTitleIndicator } from '@/components/survey/employment/special-relationships/SpecialRelationshipTitleIndicator';
import { WhyNeedSpecialRelationship } from '@/components/survey/employment/special-relationships/WhyNeedSpecialRelationship';
import { updateSpecialRelationshipsCurrentQuery } from '@/graphql/mutation/survey/employment';
import { specialRelationshipsCurrentQuery } from '@/graphql/query/survey/employment';
import { SURVEY_PAGES } from '@/lib/constants/paths';
import { useUpsertEmployment } from '@/lib/hooks/api/mutation/survey/use-upsert-employment';
import { userInputCurrentlyEmployeesAtom } from '@/lib/stores/survey/employment';
import ListIcon from '@/public/images/survey/ic-listin-right.svg';

type TempEmployee = {
  id?: string;
  name: string;
  birthday: string;
};
export const CurrentlySpecialRelationshipsContainer = ({ queryRef }: { queryRef: PreloadedQuery<employmentSpecialRelationshipsCurrentQuery> }) => {
  const router = useRouter();
  const { sendClickEvent } = useUserEventLogger();
  const eventAttributes = useSurveyUserEventAttributes();
  const { getSurveyEmploymentAnswer } = usePreloadedQuery<employmentSpecialRelationshipsCurrentQuery>(specialRelationshipsCurrentQuery, queryRef);
  const [upsertEmploymentAnswer, isLoading] = useUpsertEmployment(updateSpecialRelationshipsCurrentQuery);
  const [userInputEmployees, setUserInputEmployees] = useAtom(userInputCurrentlyEmployeesAtom);

  const prevAnswer = useMemo(
    () =>
      getSurveyEmploymentAnswer.employmentAnswer?.specialRelationshipsCurrently?.map(({ birthday, ieNm }) => ({
        name: ieNm,
        birthday: birthday ?? ''
      })) || [],
    [getSurveyEmploymentAnswer.employmentAnswer]
  );
  const prevAnswerConcatUserInputEmployees = useMemo<TempEmployee[]>(() => {
    return prevAnswer.slice().concat(userInputEmployees);
  }, [prevAnswer, userInputEmployees]);
  const selectedCount = prevAnswerConcatUserInputEmployees.length;

  const handleSubmit = () => {
    sendClickEvent('survey_special-relationships-present_confirm', eventAttributes);

    upsertEmploymentAnswer({
      variables: {
        specialRelationshipsCurrently: prevAnswerConcatUserInputEmployees.map(({ name, birthday }) => ({ ieNm: name, birthday: birthday || undefined }))
      },
      onCompleted: () => {
        setUserInputEmployees([]);
        router.push(SURVEY_PAGES.근로계약서);
      }
    });
  };

  return (
    <>
      <SurveyTopNavigation leftClickEvent={'survey_special-relationships-present_back'} />
      <main className={cn(common.main_layout, common.main_cta_space)}>
        <article>
          <SpecialRelationshipTitleIndicator />

          <h2 className={common.content_title}>
            {prevAnswerConcatUserInputEmployees.length
              ? '현재 4대보험에 가입된 직원 중,\n앞서 선택하지 않은 대표님의 특수관계인이 있다면 추가해주세요'
              : '현재 4대보험에 가입된 직원 중 대표님의 배우자나 가족, 친인척 등 특수관계인이 있다면 등록해주세요'}
          </h2>
          <WithButtonList
            className={'my-4'}
            button={{
              label: '추가',
              onClick: () => {
                sendClickEvent('survey_special-relationships-present_add', eventAttributes);
                router.push(`${SURVEY_PAGES.대표특관자_현재}/form`);
              }
            }}
          >
            <p className={cn(listStyle.with_button_list_over_text, 'mb-1')}>이름과 생년월일이 필요해요</p>
            <p className={listStyle.with_button_list_title}>특수관계인을 추가해주세요</p>
          </WithButtonList>

          {prevAnswerConcatUserInputEmployees.length > 0 && (
            <section>
              <p className={common.section_title}>
                현재 4대보험에 가입된 직원 중<br />
                {selectedCount}명의 특수관계인 직원이 등록됐어요
              </p>
              <ul>
                {prevAnswerConcatUserInputEmployees?.map(({ id, name, birthday }, idx) => (
                  <li
                    key={idx}
                    className={'border-b border-b-gray-100 last:border-b-0'}
                    onClick={() => {
                      if (!id) return;
                      sendClickEvent('survey_special-relationships-present_info', eventAttributes);
                      router.push(`${SURVEY_PAGES.대표특관자_현재}/form?id=${id}`);
                    }}
                  >
                    {id ? (
                      // 유저가 직접 추가한 직원
                      // NOTE: 추후 직접 추가한 직원은 뱃지가 들어가기 때문에 컴포넌트 분리
                      <div className={'flex items-center py-4 gap-5'}>
                        <p className={'flex-1 body-16 text-gray-800'}>
                          {name} {birthday && `(${birthToYYMD(birthday)})`}
                        </p>
                        <span className={'text-gray-300 shrink-0'}>
                          <ListIcon width={18} height={24} />
                        </span>
                      </div>
                    ) : (
                      // 앞서 선택한 직원
                      <div className={'py-4'}>
                        <p className={'body-16 text-gray-800'}>
                          {name} {birthday && `(${birthToYYMD(birthday)})`}
                        </p>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            </section>
          )}

          <WhyNeedSpecialRelationship />
        </article>

        <CTA onClick={handleSubmit} disabled={isLoading} state={selectedCount <= 0 ? 'basic' : 'primary'}>
          {selectedCount > 0 ? `총 ${selectedCount}명 추가 완료` : '해당 없어요'}
        </CTA>
      </main>
    </>
  );
};
